import * as React from "react";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { QUERY_STALE_TIME, qk } from "@repo/lib";
import { ToastProvider, useToast } from "../components/toast";
import { getMutationErrorMessage } from "../lib/mutation-error";
import { ConfirmProvider } from "../components/confirm-dialog";
import { PromptProvider } from "../components/prompt-dialog";
import { AuthProvider } from "./auth-provider";
import { PlatformAdminProvider } from "./platform-admin-provider";

type ToastApi = ReturnType<typeof useToast>;

// QueryClient 建立在 ToastProvider 外面,由 ToastBridge 把 toast 掛到這個 holder 供 MutationCache 使用
const toastHolder: { current: ToastApi | null } = { current: null };

const ToastBridge: React.FC = () => {
  const toast = useToast();
  React.useEffect(() => {
    toastHolder.current = toast;
    return () => {
      toastHolder.current = null;
    };
  }, [toast]);
  return null;
};

export const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queryClient] = React.useState(() => {
    const client = new QueryClient({
      mutationCache: new MutationCache({
        // 全域兜底:該 mutation 自己有 onError 時不重複提示
        onError: (error, _variables, _context, mutation) => {
          if (mutation.options.onError) return;
          const message = getMutationErrorMessage(error);
          if (message) toastHolder.current?.error(message);
        },
      }),
      defaultOptions: {
        queries: {
          retry: 1,
          retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
          staleTime: QUERY_STALE_TIME.standard,
          refetchOnWindowFocus: false,
          refetchOnReconnect: true,
        },
      },
    });

    for (const key of [
      qk.systemSettings(),
      qk.subscriptionCurrent(),
      qk.subscriptionPlans(),
      qk.userModules(),
      qk.availablePaymentMethods(),
      qk.serviceCategories(),
      qk.productCategories(),
      qk.customers(),
      qk.staffs(),
    ]) {
      client.setQueryDefaults(key, { staleTime: QUERY_STALE_TIME.reference });
    }

    return client;
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <PromptProvider>
            <ToastBridge />
            <PlatformAdminProvider>
              <AuthProvider>{children}</AuthProvider>
            </PlatformAdminProvider>
          </PromptProvider>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
};
